/**
 * IFC Import/Export for Interoperability
 *
 * Provides IFC4 compatibility for model exchange.
 *
 * ⚠ EXPERIMENTAL (2026-09-15 audit): the STEP writer below emits a
 * schema-incomplete document (flat entity lines only — no IfcOwnerHistory,
 * placement or product-definition hierarchy), so third-party IFC readers
 * cannot open the result. Every user-facing export path labels its output as
 * experimental instead of pretending coordination quality. Import is a stub
 * pending a real STEP parser.
 */

import { Project, BIMElement } from '../core/types';
import { v4 as uuidv4 } from 'uuid';

export interface IFCEntity {
    id: string;
    type: string;
    name?: string;
    properties: Record<string, any>;
    geometry?: any;
    relationships: string[];
}

export interface IFCModel {
    header: {
        description: string;
        implementation: string;
        name: string;
        author: string;
        organization: string;
        timestamp: string;
        schema: 'IFC4' | 'IFC4x3' | 'IFC2x3';
    };
    entities: IFCEntity[];
}

/**
 * Export project to IFC format (simplified)
 */
export function exportToIFC(project: Project): string {
    const ifcModel: IFCModel = {
        header: {
            description: 'IFC4 Export from BIM Clone',
            implementation: 'BIMClone 2025',
            name: project.name,
            author: 'User',
            organization: '',
            timestamp: new Date().toISOString(),
            schema: 'IFC4'
        },
        entities: []
    };

    // Convert project structure
    const projectEntity: IFCEntity = {
        id: `#${getNextId()}`,
        type: 'IfcProject',
        name: project.name,
        properties: {
            GlobalId: project.id,
            Name: project.name,
            LongName: project.name
        },
        relationships: []
    };
    ifcModel.entities.push(projectEntity);

    // Convert levels to IfcBuildingStorey
    project.levels.forEach(level => {
        const storeyEntity: IFCEntity = {
            id: `#${getNextId()}`,
            type: 'IfcBuildingStorey',
            name: level.name,
            properties: {
                GlobalId: level.id,
                Name: level.name,
                Elevation: level.elevation
            },
            relationships: [projectEntity.id]
        };
        ifcModel.entities.push(storeyEntity);
    });

    // Convert elements
    project.elements.forEach(element => {
        const ifcEntity = convertElementToIFC(element);
        if (ifcEntity) {
            ifcModel.entities.push(ifcEntity);
        }
    });

    // Generate IFC STEP format (simplified)
    return generateIFCString(ifcModel);
}

let entityCounter = 1;
function getNextId(): number {
    return entityCounter++;
}

function resetCounter(): void {
    entityCounter = 1;
}

function convertElementToIFC(element: BIMElement): IFCEntity | null {
    const typeMapping: Record<string, string> = {
        'Walls': 'IfcWall',
        'Doors': 'IfcDoor',
        'Windows': 'IfcWindow',
        'Floors': 'IfcSlab',
        'Roofs': 'IfcRoof',
        'Structural Columns': 'IfcColumn',
        'Structural Framing': 'IfcBeam',
        'Ducts': 'IfcDuctSegment',
        'Pipes': 'IfcPipeSegment',
    };

    const ifcType = typeMapping[element.category];
    if (!ifcType) return null;

    return {
        id: `#${getNextId()}`,
        type: ifcType,
        name: element.type,
        properties: {
            GlobalId: element.id,
            Name: element.type,
            ...element.parameters
        },
        geometry: (element as any).geometryData,
        relationships: element.constraints.map(c => c.targetId)
    };
}

function generateIFCString(model: IFCModel): string {
    resetCounter();

    let output = `ISO-10303-21;
HEADER;
FILE_DESCRIPTION(('${model.header.description}'),'2;1');
FILE_NAME('${model.header.name}.ifc','${model.header.timestamp}',('${model.header.author}'),('${model.header.organization}'),'${model.header.implementation}','Yap','$');
FILE_SCHEMA(('${model.header.schema}'));
ENDSEC;
DATA;
`;

    model.entities.forEach((entity, index) => {
        output += `#${index + 1}=${entity.type.toUpperCase()}('${entity.properties.GlobalId}',#2,$,'${entity.name || ''}',$,$,$);\n`;
    });

    output += `ENDSEC;
END-ISO-10303-21;`;

    return output;
}

/**
 * Import IFC model (stub - would need actual parser)
 */
export function importFromIFC(ifcContent: string): Partial<Project> {
    // This would need a full IFC parser like IFC.js
    // For now, return a stub
    console.log('IFC import stub - would parse:', ifcContent.substring(0, 100));

    return {
        id: uuidv4(),
        name: 'Imported IFC Model',
        elements: new Map(),
        views: new Map(),
        levels: new Map(),
    };
}

/**
 * Export mapping configuration
 */
export const IFC_EXPORT_SETTINGS = {
    exportBaseQuantities: true,
    exportBoundingBox: true,
    use2DForPlanViews: false,
    exportLinkedFiles: true,
    splitWallsAndColumns: true,
    exportRooms: true,
    exportAreas: true,
    exportSchedules: false,
    ifcVersion: 'IFC4' as const
};
